import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './Documentation.css';
import './LegacyDocs.jsx';
import LegacyDocs from "./LegacyDocs";

function Documentation() {
    return (
        <div className="doc-container">

            {/* Top Navigation: Button to access legacy documentation */}
            <div className="top-navigation">
                <a href="/legacy" className="legacy-button">
                    View Legacy Documentation
                </a>
            </div>

            <header className="doc-header">
                <h1>Promo Language Documentation</h1>
                <p>
                    Promo is a Lisp‐like, dynamically typed programming language that blends traditional S‐expression code with plain language instructions.
                    Standard code is written in parentheses ( ), while natural language instructions are enclosed in square brackets [ ].
                    This design lets you describe what you want in an intuitive way, without needing to know how these features work under the hood.
                </p>
            </header>

            <section className="doc-section">
                <h2>Table of Contents</h2>
                <ul>
                    <li><a href="#introduction">1. Introduction</a></li>
                    <li>
                        <a href="#overview">2. Language Overview &amp; Features</a>
                        <ul>
                            <li><a href="#s-expression">2.a. S-Expression Syntax</a></li>
                            <li><a href="#nl-directives">2.b. Natural Language Directives</a></li>
                            <li><a href="#ai-assisted">2.c. AI-Assisted Code Generation</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#data-types">3. Data Types</a>
                        <ul>
                            <li><a href="#numbers">3.a. Numbers</a></li>
                            <li><a href="#strings">3.b. Strings</a></li>
                            <li><a href="#variables">3.c. Variables</a></li>
                            <li><a href="#lists">3.d. Lists</a></li>
                            <li><a href="#structs">3.e. Structs</a></li>
                            <li><a href="#hashes">3.f. Hashes (Dictionaries)</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#control-structures">4. Control Structures</a>
                        <ul>
                            <li><a href="#if-expressions">4.a. if Expressions</a></li>
                            <li><a href="#while-loops">4.b. while Loops</a></li>
                            <li><a href="#for-loops">4.c. for Loops</a></li>
                            <li><a href="#let-bindings">4.d. let Bindings</a></li>
                            <li><a href="#begin-blocks">4.e. begin Blocks</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#functions">5. Functions</a>
                        <ul>
                            <li><a href="#defining-functions">5.a. Defining Functions</a></li>
                            <li><a href="#function-calls">5.b. Function Calls</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#special-forms">6. Special Forms</a>
                        <ul>
                            <li><a href="#define-set">6.a. define and set</a></li>
                            <li><a href="#ai-operator">6.b. ai Operator</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#data-structure-ops">7. Data Structure Operations</a>
                        <ul>
                            <li><a href="#list-operations">7.a. List Operations</a></li>
                            <li><a href="#hash-operations">7.b. Hash Operations</a></li>
                            <li><a href="#struct-operations">7.c. Struct Operations</a></li>
                        </ul>
                    </li>
                    <li><a href="#python-interop">8. Interoperability with Python</a></li>
                    <li><a href="#examples">9. Examples</a></li>
                    <li><a href="#conclusion">10. Conclusion</a></li>
                </ul>
            </section>

            <section className="doc-section" id="introduction">
                <h2>1. Introduction</h2>
                <p>
                    Promo is designed to combine the clarity of traditional programming with the ease of expressing intent in natural language.
                    By mixing descriptive instructions (using square brackets) with precise Lisp-style code (using parentheses),
                    Promo simplifies prototyping and dynamic code generation.
                </p>
            </section>

            <section className="doc-section" id="overview">
                <h2>2. Language Overview &amp; Features</h2>

                <h3 id="s-expression">2.a. S-Expression Syntax</h3>
                <p>
                    Promo preserves the simplicity of Lisp: every expression is written in prefix notation and enclosed in parentheses.
                    For example:
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define x 10)`}
                </SyntaxHighlighter>

                <h3 id="nl-directives">2.b. Natural Language Directives</h3>
                <p>
                    Promo lets you include natural language directives directly in your code by using square brackets.
                    Simply write your instruction in plain language, and the system will ensure the desired behavior is achieved.
                    The underlying mechanism is fully abstracted – you only need to know that your instruction will work as intended.
                </p>
                <p>For example:</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define (increment x) [add one to x])`}
                </SyntaxHighlighter>

                <h3 id="ai-assisted">2.c. AI-Assisted Code Generation</h3>
                <p>
                    Promo also offers an AI-assisted feature that simplifies function generation.
                    With the <code>ai</code> operator, you describe a function’s behavior in plain language,
                    and it produces a function that behaves accordingly.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(ai "add" 1 2)`}
                </SyntaxHighlighter>
                <p>
                    In this example, the operator creates a function that sums the two numbers.
                </p>
            </section>

            <section className="doc-section" id="data-types">
                <h2>3. Data Types</h2>
                <h3 id="numbers">3.a. Numbers</h3>
                <p>Both integers and floating-point numbers are supported.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define a 5)`}
                </SyntaxHighlighter>

                <h3 id="strings">3.b. Strings</h3>
                <p>Strings must be enclosed in double quotes.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define greeting "Hello, World!")`}
                </SyntaxHighlighter>

                <h3 id="variables">3.c. Variables</h3>
                <p>
                    Define variables with <code>define</code> and update them using <code>set</code>.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define x 42)
(set x 100)`}
                </SyntaxHighlighter>

                <h3 id="lists">3.d. Lists</h3>
                <p>
                    Create lists using the <code>list</code> keyword or <code>cons</code>.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define my-list (list 1 2 3))
(define another-list (cons 0 my-list))`}
                </SyntaxHighlighter>

                <h3 id="structs">3.e. Structs</h3>
                <p>
                    Define custom data structures with <code>struct</code>.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(struct Point (x y))
(define p (Point 10 20))`}
                </SyntaxHighlighter>

                <h3 id="hashes">3.f. Hashes (Dictionaries)</h3>
                <p>
                    Hashes organize key-value pairs.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define my-hash (hash "key1" "value1" "key2" "value2"))`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="control-structures">
                <h2>4. Control Structures</h2>
                <h3 id="if-expressions">4.a. if Expressions</h3>
                <p>
                    Use <code>if</code> for conditional execution (0 is false; any other number is true):
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(if (> x 0) (print "Positive") (print "Non-positive"))`}
                </SyntaxHighlighter>

                <h3 id="while-loops">4.b. while Loops</h3>
                <p>
                    Execute expressions repeatedly while the condition holds:
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(while (< i 10)
  (print i)
  (set i (+ i 1)))`}
                </SyntaxHighlighter>

                <h3 id="for-loops">4.c. for Loops</h3>
                <p>
                    Iterate through all the elements of a list:
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(for (item my-list)
  (print item))`}
                </SyntaxHighlighter>

                <h3 id="let-bindings">4.d. let Bindings</h3>
                <p>Create local variable bindings with <code>let</code>:</p>
                <SyntaxHighlighter language="lisp" >
                    {`(let ((x 10) (y 20))
  (print (+ x y)))`}
                </SyntaxHighlighter>

                <h3 id="begin-blocks">4.e. begin Blocks</h3>
                <p>
                    Group multiple expressions that are evaluated sequentially:
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(begin (define x 1)
  (set x (+ x 1))
  x)`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="functions">
                <h2>5. Functions</h2>
                <h3 id="defining-functions">5.a. Defining Functions</h3>
                <p>
                    Define functions using <code>defn</code> or with a <code>define</code> form. For example:
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define (square x)
  (* x x))`}
                </SyntaxHighlighter>

                <h3 id="function-calls">5.b. Function Calls</h3>
                <p>
                    Invoke functions by placing the name as the first element of an s-expression.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(square 5) ; Returns 25`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="special-forms">
                <h2>6. Special Forms</h2>
                <h3 id="define-set">6.a. define and set</h3>
                <p>
                    Declare variables or functions with <code>define</code> and update them with <code>set</code>.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define pi 3.14)
(set pi 3.1415)`}
                </SyntaxHighlighter>

                <h3 id="ai-operator">6.b. ai Operator</h3>
                <p>
                    The <code>ai</code> operator offers a simple way to generate code from a plain language description.
                    You only need to state what you want, and the operator produces code that behaves accordingly.
                    No knowledge of the internal workings is required.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(ai "generate-summary" someText)`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="data-structure-ops">
                <h2>7. Data Structure Operations</h2>
                <h3 id="list-operations">7.a. List Operations</h3>
                <ul>
                    <li><code>cons</code>: Prepend an element to a list.</li>
                    <li><code>car</code>: Retrieve the first element.</li>
                    <li><code>cdr</code>: Retrieve the remainder of the list.</li>
                    <li><code>null</code>: Represents an empty list.</li>
                </ul>
                <SyntaxHighlighter language="lisp" >
                    {`(define lst (cons 1 (cons 2 null)))
(car lst) ; Returns 1
(cdr lst) ; Returns (2)`}
                </SyntaxHighlighter>

                <h3 id="hash-operations">7.b. Hash Operations</h3>
                <ul>
                    <li><code>hash</code>: Create a new hash (dictionary).</li>
                    <li><code>get</code>: Retrieve a value by its key.</li>
                </ul>
                <SyntaxHighlighter language="lisp" >
                    {`(define h (hash "name" "Alice" "age" 30))
(get h "name") ; Returns "Alice"`}
                </SyntaxHighlighter>

                <h3 id="struct-operations">7.c. Struct Operations</h3>
                <p>
                    Define data structures with <code>struct</code> and access them using constructors and accessors.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(struct Person (name age))
(define p (Person "Bob" 25))
(Person-name p) ; Returns "Bob"
(Person-age p)  ; Returns 25
(Person? p)     ; Returns true`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="python-interop">
                <h2>8. Interoperability with Python</h2>
                <p>
                    Promo is implemented in Python, allowing seamless integration with Python code. Functions and variables can interact directly,
                    and the <code>ai</code> operator may also incorporate Python functionality as needed.
                </p>
            </section>

            <section className="doc-section" id="examples">
                <h2>9. Examples</h2>
                <h3>Example 1: Traditional Function Definition</h3>
                <SyntaxHighlighter language="lisp" >
                    {`(define (add a b)
  (+ a b))
(add 5 7) ; Returns 12`}
                </SyntaxHighlighter>

                <h3>Example 2: Using Control Structures</h3>
                <SyntaxHighlighter language="lisp" >
                    {`(define i 0)
(while (< i 5)
  (print i)
  (set i (+ i 1)))`}
                </SyntaxHighlighter>

                <h3>Example 3: Embedding Natural Language Directives</h3>
                <p>
                    In this example, a natural language directive is used to define a function concisely.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define (increment x)
  [add one to x])
(increment 10) ; Returns 11`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="conclusion">
                <h2>10. Conclusion</h2>
                <p>
                    Promo represents an innovative fusion of Lisp-style code and natural language. By abstracting the complexity of AI-based code generation,
                    it empowers you to write clear, intention-revealing code without needing to know the underlying implementation details.
                </p>
                <p>
                    Use Promo to bridge the gap between human intent and machine execution.
                </p>
            </section>

        </div>
    );
}

export default Documentation;
